import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { DataTable } from '@/components/ui/data-table';
import { useToast } from '@/hooks/useToast';
import { useCurrencies, useActiveCurrencies, useExchangeRates, useCreateExchangeRate, useCurrencyConversion } from '@/hooks/useCurrencies';
import { ModalForm, type FormField } from '@/components/ui/modal-form';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  DollarSign,
  Plus,
  ArrowRightLeft,
  TrendingUp,
  ArrowDownRight,
  ArrowUpRight,
} from 'lucide-react';

export default function CurrenciesPage() {
  const { toast } = useToast();
  const [isAddRateModalOpen, setIsAddRateModalOpen] = useState(false);
  const [isConvertModalOpen, setIsConvertModalOpen] = useState(false);
  const [conversionResult, setConversionResult] = useState<any>(null);

  const { data: currencies = [] } = useCurrencies();
  const { data: activeCurrencies = [] } = useActiveCurrencies();
  const { data: exchangeRates = [] } = useExchangeRates({ active_only: true });

  const createRateMutation = useCreateExchangeRate();
  const conversionMutation = useCurrencyConversion();

  const addRateFields: FormField[] = [
    { name: 'from_currency', label: 'From Currency', type: 'select', required: true,
      options: currencies.map(c => ({ value: c.currency_code, label: `${c.currency_code} - ${c.name}` })) },
    { name: 'to_currency', label: 'To Currency', type: 'select', required: true,
      options: currencies.map(c => ({ value: c.currency_code, label: `${c.currency_code} - ${c.name}` })) },
    { name: 'rate', label: 'Exchange Rate', type: 'number', required: true, description: '1 from_currency = ? to_currency' },
    { name: 'effective_date', label: 'Effective Date', type: 'date', required: true },
    { name: 'expiry_date', label: 'Expiry Date', type: 'date' },
    { name: 'source', label: 'Source', type: 'select',
      options: [
        { value: 'manual', label: 'Manual Entry' },
        { value: 'api', label: 'API Feed' },
        { value: 'bank', label: 'Bank Rate' },
      ]},
  ];

  const handleAddRate = async (data: any) => {
    try {
      await createRateMutation.mutateAsync(data);
      toast({ title: 'Exchange rate added successfully' });
      setIsAddRateModalOpen(false);
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  const handleConvert = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const amount = parseFloat(formData.get('amount') as string);
    const fromCurrency = formData.get('from_currency') as string;
    const toCurrency = formData.get('to_currency') as string;

    try {
      const result = await conversionMutation.mutateAsync({
        amount,
        fromCurrency,
        toCurrency,
      });
      setConversionResult(result);
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  const baseCurrency = currencies.find(c => c.is_base_currency);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Multi-Currency</h1>
          <p className="text-muted-foreground">Manage currencies and exchange rates</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setIsConvertModalOpen(true)}>
            <ArrowRightLeft className="h-4 w-4 mr-2" />
            Convert
          </Button>
          <Button onClick={() => setIsAddRateModalOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Add Rate
          </Button>
        </div>
      </div>

      {/* Base Currency Info */}
      <Card className="bg-primary/5 border-primary/20">
        <CardContent className="pt-6">
          <div className="flex items-center gap-6">
            <div className="p-4 bg-primary/10 rounded-full">
              <DollarSign className="h-8 w-8 text-primary" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Base Currency</p>
              <p className="text-2xl font-bold">
                {baseCurrency?.currency_code} - {baseCurrency?.name}
              </p>
              <p className="text-sm text-muted-foreground">
                Symbol: {baseCurrency?.symbol} • {baseCurrency?.decimal_places} decimal places
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Currencies Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {currencies.map((currency: any) => (
          <Card key={currency.currency_code}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="text-2xl font-bold">{currency.symbol}</div>
                  <div>
                    <p className="font-medium">{currency.currency_code}</p>
                    <p className="text-sm text-muted-foreground">{currency.name}</p>
                  </div>
                </div>
                {currency.is_base_currency && (
                  <Badge variant="outline">Base</Badge>
                )}
              </div>
              <div className="flex items-center gap-2 text-sm">
                {currency.rate_to_base ? (
                  <>
                    <span className="text-muted-foreground">1 = </span>
                    <span className="font-medium">
                      {currency.rate_to_base.toFixed(4)} {baseCurrency?.currency_code}
                    </span>
                    {currency.rate_to_base < 1 ? (
                      <ArrowDownRight className="h-4 w-4 text-red-500" />
                    ) : (
                      <ArrowUpRight className="h-4 w-4 text-green-500" />
                    )}
                  </>
                ) : (
                  <span className="text-muted-foreground">Base Currency</span>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Exchange Rates Table */}
      <Card>
        <CardHeader>
          <CardTitle>Exchange Rates</CardTitle>
        </CardHeader>
        <CardContent>
          <DataTable
            columns={[
              { accessorKey: 'from_currency', header: 'From' },
              { 
                id: 'rate',
                header: 'Rate',
                cell: ({ row }: any) => (
                  <span className="font-mono">{row.rate.toFixed(6)}</span>
                ),
              },
              { accessorKey: 'to_currency', header: 'To' },
              { 
                accessorKey: 'effective_date',
                header: 'Effective',
                cell: ({ row }: any) => new Date(row.effective_date).toLocaleDateString(),
              },
              { accessorKey: 'source', header: 'Source' },
              {
                id: 'status',
                header: 'Status',
                cell: ({ row }: any) => (
                  <Badge className={row.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100'}>
                    {row.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                ),
              },
            ]}
            data={exchangeRates}
          />
        </CardContent>
      </Card>

      {/* Add Rate Modal */}
      <ModalForm
        isOpen={isAddRateModalOpen}
        onClose={() => setIsAddRateModalOpen(false)}
        onSubmit={handleAddRate}
        fields={addRateFields}
        title="Add Exchange Rate"
        isLoading={createRateMutation.isPending}
      />

      {/* Convert Modal */}
      <Dialog open={isConvertModalOpen} onOpenChange={setIsConvertModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Currency Converter</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleConvert} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Amount</label>
              <input
                type="number"
                name="amount"
                step="0.01"
                required
                className="w-full px-4 py-2 border rounded-lg"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">From</label>
                <select name="from_currency" required className="w-full px-4 py-2 border rounded-lg">
                  {currencies.map((c: any) => (
                    <option key={c.currency_code} value={c.currency_code}>
                      {c.currency_code}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">To</label>
                <select name="to_currency" required className="w-full px-4 py-2 border rounded-lg">
                  {currencies.map((c: any) => (
                    <option key={c.currency_code} value={c.currency_code}>
                      {c.currency_code}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <Button type="submit" className="w-full" isLoading={conversionMutation.isPending}>
              Convert
            </Button>
          </form>
          
          {conversionResult && (
            <div className="mt-6 p-4 bg-muted rounded-lg text-center">
              <p className="text-sm text-muted-foreground">
                {conversionResult.original_amount.toLocaleString()} {conversionResult.from_currency}
              </p>
              <p className="text-3xl font-bold">
                {conversionResult.converted_amount.toLocaleString(undefined, { minimumFractionDigits: 2 })} {conversionResult.to_currency}
              </p>
              <p className="text-sm text-muted-foreground mt-2">
                Rate: 1 {conversionResult.from_currency} = {conversionResult.exchange_rate.toFixed(6)} {conversionResult.to_currency}
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
